import { Box, type SxProps, type Theme } from "@mui/material";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { GRID_RADIUS, GRID_Z, NEUTRAL, SHADOW } from "../../../../theme/tokens";

type Props = {
	/** WAI-ARIA grid label naming the view (already translated). */
	ariaLabel: string;
	dataTestId: string;
	/** CSS `grid-template-columns` for this grid's lattice (from `gridGeometry`). */
	gridTemplateColumns: string;
	/** Extra sx merged onto the scroll grid (e.g. a grid's week-header hover-tint rules). */
	containerSx?: SxProps<Theme>;
	/** Forwarded to the scroll grid element (e.g. linked-hover `gridRef`). */
	gridRef?: React.RefObject<HTMLDivElement | null>;
	children: ReactNode;
};

// Overlay width + edge inset (px) — sit just inside the 1px border so the hint
// never paints over the rounded border or the scrollbar gutter.
const HINT_WIDTH = 28;
const EDGE_INSET = 1;

/**
 * The shared scroll viewport for every Master Schedule v3 grid — the single
 * `role="grid"` element carrying the common lattice chrome (1px gridline gap,
 * neutral border, panel radius, `overflow: auto`, `maxHeight: 100%`). Extracted
 * so the three grids can't drift on the container, and so ONE place owns:
 *  - the WAI-ARIA grid role + `aria-label`,
 *  - the trailing right-edge scroll-shadow that cues off-screen day columns.
 *
 * The scroll-shadow is an on-top overlay (the cells are opaque, so an inset
 * shadow/background wouldn't show) driven directly from the scroll position —
 * its opacity + height are mutated on the DOM node in an rAF, never via React
 * state, so scrolling triggers no re-render. It's a transparent-when-idle,
 * pointer-events-none sibling of the scroll grid; a thin relative wrapper hosts
 * it without changing the grid's own box (the wrapper is full-height and
 * transparent, and the overlay is clamped to the grid's visible height).
 */
export const GridScrollContainer = ({ ariaLabel, dataTestId, gridTemplateColumns, containerSx, gridRef, children }: Props) => {
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const overlayRef = useRef<HTMLDivElement | null>(null);
	const rafRef = useRef<number | null>(null);

	// Merge the internal scroll ref with the optional forwarded ref (linked hover).
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			if (gridRef) gridRef.current = node;
		},
		[gridRef],
	);

	const syncHint = useCallback(() => {
		const grid = scrollRef.current;
		const overlay = overlayRef.current;
		if (!grid || !overlay) return;
		// Match the overlay to the grid's visible box (clientHeight excludes any
		// horizontal scrollbar), then show it only while more content sits to the right.
		overlay.style.height = `${grid.clientHeight - EDGE_INSET * 2}px`;
		const maxScrollLeft = grid.scrollWidth - grid.clientWidth;
		overlay.style.opacity = maxScrollLeft > 1 && grid.scrollLeft < maxScrollLeft - 1 ? "1" : "0";
	}, []);

	const onScroll = useCallback(() => {
		if (rafRef.current !== null) return;
		rafRef.current = requestAnimationFrame(() => {
			rafRef.current = null;
			syncHint();
		});
	}, [syncHint]);

	useEffect(() => {
		const grid = scrollRef.current;
		if (!grid) return;
		syncHint();
		const observer = new ResizeObserver(syncHint);
		observer.observe(grid);
		return () => {
			observer.disconnect();
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
		};
	}, [syncHint]);

	return (
		<Box sx={{ position: "relative", height: "100%", minHeight: 0 }}>
			<Box
				ref={setScrollRef}
				role="grid"
				aria-label={ariaLabel}
				data-testid={dataTestId}
				onScroll={onScroll}
				sx={[
					{
						display: "grid",
						gridTemplateColumns,
						gap: "1px",
						backgroundColor: NEUTRAL[200],
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: GRID_RADIUS.PANEL,
						overflow: "auto",
						maxHeight: "100%",
					},
					...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : []),
				]}
			>
				{children}
			</Box>
			{/* Trailing scroll cue — opacity + height set imperatively in `syncHint`. */}
			<Box
				ref={overlayRef}
				aria-hidden
				sx={{
					position: "absolute",
					top: EDGE_INSET,
					right: EDGE_INSET,
					width: HINT_WIDTH,
					opacity: 0,
					pointerEvents: "none",
					backgroundImage: SHADOW.SCROLL_HINT_RIGHT,
					transition: "opacity 160ms cubic-bezier(0.23, 1, 0.32, 1)",
					zIndex: GRID_Z.CORNER + 1,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			/>
		</Box>
	);
};
